"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCookieDomain = exports.getTopLevelDomain = exports.userOptedOutOfEssentialCookies = exports.getUpdatedWebClientIdSetCookieString = exports.getNewEssentialSessionCookieString = exports.getNewEssentialSessionCookie = exports.getUpdatedWebClientIdCookie = exports.getWebClientId = exports.hasOptedOutFromEssentialSession = exports.getScopedCookieDomain = exports.tryGetCookie = exports.hasNonceCookieSet = exports.hasAcceptedSCCookies = exports.SC_WEB_CLIENT_ID_NAME = exports.ESSENTIAL_SESSION_COOKIE_NAME = void 0;
const graphene_1 = __importDefault(require("@snapchat/graphene"));
const cookie_1 = require("cookie");
const js_cookie_1 = __importDefault(require("js-cookie"));
const uuid_1 = require("uuid");
const parse_domain_1 = require("parse-domain");
const SC_ALLOWED_DOMAINS = new Set([".snapchat.com", ".snap-dev.net", "localhost"]);
const SC_ACCEPTED_COOKIES_NAME = "sc-cookies-accepted";
const SC_A_NONCE_COOKIE_NAME = "sc-a-nonce";
exports.ESSENTIAL_SESSION_COOKIE_NAME = "EssentialSession";
exports.SC_WEB_CLIENT_ID_NAME = "sc-wcid";
/* istanbul ignore next : Don't test library getter */
/**
 * WARNING: you need to first accept the cookie notice on prod, otherwise this will always return false. This is because we don't render Cookie Popup Banner on dev.
 */
const hasAcceptedSCCookies = () => js_cookie_1.default.get(SC_ACCEPTED_COOKIES_NAME) === "true";
exports.hasAcceptedSCCookies = hasAcceptedSCCookies;
const hasNonceCookieSet = () => js_cookie_1.default.get(SC_A_NONCE_COOKIE_NAME) !== undefined;
exports.hasNonceCookieSet = hasNonceCookieSet;
function tryGetCookie(cookies, cookieName) {
    if (!cookies || typeof cookies !== "string") {
        return undefined;
    }
    const parsed = (0, cookie_1.parse)(cookies);
    return parsed[cookieName];
}
exports.tryGetCookie = tryGetCookie;
function getScopedCookieDomain(domain) {
    const subDomain = domain.substring(domain.indexOf("."));
    return subDomain.substring(0, subDomain.includes(":") ? subDomain.indexOf(":") : subDomain.length);
}
exports.getScopedCookieDomain = getScopedCookieDomain;
const hasOptedOutFromEssentialSession = (cookies) => tryGetCookie(cookies, exports.ESSENTIAL_SESSION_COOKIE_NAME) === "false"; // Learn about EssentialSession Cookies here - https://docs.google.com/document/d/1EjCg_7-uaVstzxDX54YhWs3WVdWsxtGow6zSnXXdGY4/edit and https://snapchat.quip.com/Lv7cAd7q5kLF
exports.hasOptedOutFromEssentialSession = hasOptedOutFromEssentialSession;
function getCookieOptions(hostHeader, expiryDate, path) {
    const baseOptions = {
        expires: new Date(expiryDate),
        path: path,
        secure: true,
        httpOnly: false,
        credentials: "same-origin",
    };
    if (hostHeader && typeof hostHeader === "string") {
        const cookieDomain = getScopedCookieDomain(hostHeader);
        if (SC_ALLOWED_DOMAINS.has(cookieDomain)) {
            return {
                ...baseOptions,
                domain: cookieDomain,
            };
        }
    }
    return baseOptions;
}
function getWebClientIdCookieOptions(hostHeader, isGdpr) {
    let expires;
    if (isGdpr) {
        // For GDPR countries, the expiry of the web_client_id should be set to 24 hours
        expires = new Date();
        expires.setDate(expires.getDate() + 1);
    }
    else {
        expires = new Date();
        expires.setFullYear(expires.getFullYear() + 1);
    }
    return getCookieOptions(hostHeader, expires, "/");
}
const getWebClientId = (cookies) => {
    if ((0, exports.hasOptedOutFromEssentialSession)(cookies)) {
        js_cookie_1.default.remove(exports.SC_WEB_CLIENT_ID_NAME);
        return undefined;
    }
    return tryGetCookie(cookies, exports.SC_WEB_CLIENT_ID_NAME);
};
exports.getWebClientId = getWebClientId;
const getUpdatedWebClientIdCookie = (cookies, host, isGdpr = true) => {
    if ((0, exports.hasOptedOutFromEssentialSession)(cookies)) {
        js_cookie_1.default.remove(exports.SC_WEB_CLIENT_ID_NAME);
        return undefined;
    }
    const currentWebClientId = tryGetCookie(cookies, exports.SC_WEB_CLIENT_ID_NAME);
    const webClientId = currentWebClientId ? currentWebClientId : (0, uuid_1.v4)();
    const webClientIdOptions = getWebClientIdCookieOptions(host, isGdpr);
    return {
        key: exports.SC_WEB_CLIENT_ID_NAME,
        value: webClientId,
        options: webClientIdOptions,
    };
};
exports.getUpdatedWebClientIdCookie = getUpdatedWebClientIdCookie;
function getNewEssentialSessionCookie(cookies, host) {
    if ((0, exports.hasOptedOutFromEssentialSession)(cookies)) {
        if (graphene_1.default.isReady()) {
            graphene_1.default.increment({ metricsName: "Cookies.EssentialSession.OptOut", value: 1 });
        }
        return;
    }
    const existingCookie = tryGetCookie(cookies, exports.ESSENTIAL_SESSION_COOKIE_NAME);
    if (!existingCookie) {
        const expires = new Date();
        expires.setFullYear(expires.getFullYear() + 1);
        return {
            key: exports.ESSENTIAL_SESSION_COOKIE_NAME,
            value: "true",
            options: getCookieOptions(host, expires, "/"),
        };
    }
    // If Essential Cookie is already set, no new cookie needs to be set
    return undefined;
}
exports.getNewEssentialSessionCookie = getNewEssentialSessionCookie;
function getNewEssentialSessionCookieString(cookies, host) {
    const cookie = getNewEssentialSessionCookie(cookies, host);
    if (cookie) {
        const { key, value, options } = cookie;
        return (0, cookie_1.serialize)(key, value, options);
    }
}
exports.getNewEssentialSessionCookieString = getNewEssentialSessionCookieString;
function getUpdatedWebClientIdSetCookieString(cookies, host) {
    const cookie = (0, exports.getUpdatedWebClientIdCookie)(cookies, host);
    if (!cookie) {
        return undefined;
    }
    const { key, value, options } = cookie;
    return (0, cookie_1.serialize)(key, value, options);
}
exports.getUpdatedWebClientIdSetCookieString = getUpdatedWebClientIdSetCookieString;
/**
 * check if we can set wcid cookie for a browser.
 * note: client-side only, uses "document"
 */
const userOptedOutOfEssentialCookies = () => {
    if (typeof document === "undefined") {
        return undefined;
    }
    const cookies = document.cookie;
    return (0, exports.hasOptedOutFromEssentialSession)(cookies);
};
exports.userOptedOutOfEssentialCookies = userOptedOutOfEssentialCookies;
/**
 * Returns the top level domain from a hostname.
 */
const getTopLevelDomain = (hostname) => {
    const results = (0, parse_domain_1.parseDomain)(hostname);
    // If an ICANN domain is present, return the domain name combined with the top level domains
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (results.icann?.domain) {
        return `${results.icann.domain}.${results.icann.topLevelDomains.join(".")}`;
    }
    else {
        return hostname;
    }
};
exports.getTopLevelDomain = getTopLevelDomain;
/**
 * Returns the cookie domain.
 */
const getCookieDomain = (cookieDomain) => {
    // Use input value if specified
    if (cookieDomain)
        return cookieDomain;
    // Use top level domain (any.snapchat.com => snapchat.com)
    const hostName = (typeof window === "undefined" ? "" : window.location.hostname).toLowerCase();
    const topLevelDomain = (0, exports.getTopLevelDomain)(hostName);
    // Return top level domain or empty string
    return topLevelDomain;
};
exports.getCookieDomain = getCookieDomain;
//# sourceMappingURL=sc-cookies.js.map